import { getFirebase } from 'firestorter';

import Collections from '../../utils/collections';

import BaseDocument from '../../Model/BaseDocument';

const UpsellTouchpointKey = {
  SEVEN_DAYS_BEFORE: 'sevenDaysBefore',
  THREE_DAYS_BEFORE: 'threeDaysBefore',
  LAST_DAY: 'lastDay',
};

const UpsellOfferType = {
  COACHING: 'COACHING',
  CHALLENGE: 'CHALLENGE',
};

const UpsellMediaType = {
  IMAGE: 'IMAGE',
  VIDEO: 'VIDEO',
};

const createEmptyTouchpoint = () => ({
  enabled: false,
  message: {
    text: '',
    mediaType: null,
    mediaUrl: '',
    mediaPath: '',
  },
  offer: null,
});

/**
 * The 1:1 coaching a client buying the challenge on Avida checkout may pre-enroll into, starting when the challenge
 * ends. Independent of the pop-ups' master switch.
 */
const createEmptyCheckoutPreEnroll = () => ({
  enabled: false,
  offer: null,
});

/**
 * The test group (group B) of the pop-up A/B test: the touchpoints a client the upsellPopupOfferExperiment flag puts in
 * `test` is shown instead of the strategy's own. A strategy has none until an admin sets one up; absent or disabled,
 * every client sees the control touchpoints. A test touchpoint with no text and no media shows the control
 * touchpoint's message (resolveTouchpointForVariant on mobile and server).
 */
const createEmptyExperiment = () => ({
  enabled: false,
  touchpoints: {
    [UpsellTouchpointKey.SEVEN_DAYS_BEFORE]: createEmptyTouchpoint(),
    [UpsellTouchpointKey.THREE_DAYS_BEFORE]: createEmptyTouchpoint(),
    [UpsellTouchpointKey.LAST_DAY]: createEmptyTouchpoint(),
  },
});

const createEmptyStrategy = () => ({
  touchpoints: {
    [UpsellTouchpointKey.SEVEN_DAYS_BEFORE]: createEmptyTouchpoint(),
    [UpsellTouchpointKey.THREE_DAYS_BEFORE]: createEmptyTouchpoint(),
    [UpsellTouchpointKey.LAST_DAY]: createEmptyTouchpoint(),
  },
  checkoutPreEnroll: createEmptyCheckoutPreEnroll(),
});

/**
 * upsellConfig/{coachId} — per-coach upsell pop-up configuration: a default
 * strategy (three touchpoints, each with its own message and offer) plus
 * per-challenge overrides keyed by the challenge BASE contract id.
 */
class UpsellConfig extends BaseDocument {
  constructor(id, opts) {
    super(`${Collections.UPSELL_CONFIG}/${id}`, opts);
  }

  get enabled() {
    return !!this.data.enabled;
  }

  get defaultStrategy() {
    return this.data.default || createEmptyStrategy();
  }

  get challenges() {
    return this.data.challenges || {};
  }

  /**
   * The strategy a challenge follows: its custom snapshot when overridden, else the default. Mirror of the server's
   * resolveStrategy (subscription/utils/upsellOffer.ts) — the two must agree, or the client is shown one offer and
   * sold another.
   *
   * @param {string} baseContractId the challenge's reusable (BASE) contract id, which keys the overrides
   */
  getStrategyForChallenge(baseContractId) {
    const entry = this.challenges[baseContractId];
    if (entry && entry.useDefault === false && entry.custom) {
      return entry.custom;
    }
    return this.data.default;
  }

  /**
   * The 1:1 offer a client buying this challenge may pre-enroll into at checkout, or null. Independent of the
   * pop-ups' master switch, and only ever coaching. Mirror of the server's resolveCheckoutPreEnrollOffer.
   *
   * @param {string} baseContractId the challenge's reusable (BASE) contract id
   */
  getCheckoutPreEnrollOffer(baseContractId) {
    const checkoutPreEnroll = this.getStrategyForChallenge(baseContractId)?.checkoutPreEnroll;
    if (!checkoutPreEnroll?.enabled || checkoutPreEnroll.offer?.type !== UpsellOfferType.COACHING) {
      return null;
    }
    return checkoutPreEnroll.offer;
  }

  static async getByCoachId(coachId) {
    const upsellConfigDoc = new UpsellConfig(coachId);
    await upsellConfigDoc.init();
    return upsellConfigDoc;
  }

  /**
   * Turns the coach's upsell pop-up on or off. Written on its own so the
   * switch takes effect immediately — clients pick it up live — instead of
   * waiting on the default strategy's save.
   */
  setEnabled = async (enabled, lastUpdatedByUserName) => this.writeAudited({ enabled }, lastUpdatedByUserName);

  /**
   * Saves the default strategy, leaving the master toggle and overrides untouched.
   * Replaces `default` outright rather than merging into it: merge recurses into
   * maps, so an offer switching type would keep the previous type's keys.
   */
  saveDefaultStrategy = async ({ defaultStrategy, lastUpdatedByUserName }) => (
    this.writeAudited({ default: defaultStrategy }, lastUpdatedByUserName)
  );

  /**
   * Saves one challenge's upsell override entry on this config doc. The
   * challenge contract itself is never touched — its id is only the key the
   * overrides map is stored under.
   */
  saveChallengeOverride = async (baseContractId, entry, lastUpdatedByUserName) => this.writeAudited(
    // Dotted so the entry is replaced outright while its siblings survive. set()
    // reads a dotted key literally, so the no-doc-yet case has to nest instead.
    this.exists
      ? { [`challenges.${baseContractId}`]: entry }
      : { challenges: { [baseContractId]: entry } },
    lastUpdatedByUserName,
  );

  /**
   * Every write goes through here. Firestore's set/update don't refresh the local
   * snapshot and nothing observes this doc, so without the re-fetch a second save
   * in the same session still believes the doc is new — rewriting createdAt and
   * taking the nested path that keeps a previous offer's keys.
   */
  writeAudited = async (fields, lastUpdatedByUserName) => {
    const wasNew = !this.exists;
    await this.updateFields({ ...fields, ...this.auditFields(lastUpdatedByUserName) });
    if (wasNew) {
      await this.fetch();
    }
  };

  auditFields = (lastUpdatedByUserName) => {
    const timestamp = getFirebase().firestore.FieldValue.serverTimestamp();
    return {
      lastUpdatedByUserName: lastUpdatedByUserName || '',
      updatedAt: timestamp,
      ...(this.data?.createdAt ? {} : { createdAt: timestamp }),
    };
  };
}

/**
 * The regular monthly price a 1:1 offer is shown discounted from, or null when it has none. Display only: a price that
 * isn't above the one charged is no discount, so it is not shown.
 *
 * @param {Object} offer a COACHING upsell offer
 * @returns {number|null} the original monthly price, in cents
 */
const getOriginalMonthlyPriceInCents = (offer) => (
  offer?.originalMonthlyPriceInCents > offer?.monthlyPriceInCents ? offer.originalMonthlyPriceInCents : null
);

export default UpsellConfig;
export {
  getOriginalMonthlyPriceInCents,
  UpsellTouchpointKey,
  UpsellOfferType,
  UpsellMediaType,
  createEmptyStrategy,
  createEmptyTouchpoint,
  createEmptyCheckoutPreEnroll,
  createEmptyExperiment,
};
